"use client";

import type { ComponentType, SVGProps } from "react";
import { Button } from "../../../elements/button/button";
import { cn } from "../../../lib/utils";
import { footerClasses } from "../footer.constants";
import type { FooterSocialLink, FooterSocialPlatform } from "../footer.types";
import {
  FooterFacebookIcon,
  FooterInstagramIcon,
  FooterLinkedinIcon,
  FooterPinterestIcon,
  FooterTiktokIcon,
  FooterXIcon,
  FooterYoutubeIcon,
} from "./footer-social-icons";

const SOCIAL_PLATFORMS: Record<
  FooterSocialPlatform,
  { label: string; Icon: ComponentType<SVGProps<SVGSVGElement>> }
> = {
  facebook: { label: "Facebook", Icon: FooterFacebookIcon },
  instagram: { label: "Instagram", Icon: FooterInstagramIcon },
  youtube: { label: "YouTube", Icon: FooterYoutubeIcon },
  linkedin: { label: "LinkedIn", Icon: FooterLinkedinIcon },
  tiktok: { label: "TikTok", Icon: FooterTiktokIcon },
  pinterest: { label: "Pinterest", Icon: FooterPinterestIcon },
  x: { label: "X", Icon: FooterXIcon },
};

export interface FooterSocialLinksProps {
  links: FooterSocialLink[];
  /** Accessible name of the list, e.g. "Follow us". */
  label: string;
  className?: string;
}

/** Icon links to the brand's social media profiles, named by platform. */
export function FooterSocialLinks(props: Readonly<FooterSocialLinksProps>) {
  return (
    <ul
      aria-label={props.label}
      className={cn("flex flex-wrap justify-center gap-9", props.className)}
    >
      {props.links.map((link) => {
        const { label, Icon } = SOCIAL_PLATFORMS[link.platform];

        return (
          <li key={`${link.platform}-${link.href}`}>
            {/* The D2C mode sizes icons to 20px; these keep their 27px. */}
            <Button
              aria-label={label}
              className={cn(
                footerClasses.focusRing,
                "block text-alabastergrey transition-colors hover:text-white motion-reduce:transition-none [&_svg:not(.button-loader)]:size-auto"
              )}
              href={link.href}
              mode="D2C"
              unstyled={true}
            >
              <Icon />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
